import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useProjectContext } from "../../context/useProjectContext";
import SubscriptionDetails from "./SubscriptionDetails";
import PageHeader from "../../Components/Layout/PageHeader";
import OutlineButton from "../../Components/Generic/OutlineButton";
import { useOutletContext, useNavigate } from "react-router-dom";
import SubscriptionProductCard from "../../Components/SubscriptionsComponents/SubscriptionProductCard";
import NoProductSubscriptionCard from "../../Components/SubscriptionsComponents/NoProductSubscriptionCard";
import SubscriptionSummaryBar from "../../Components/SubscriptionsComponents/SubscriptionSummaryBar";
import { PRODUCTS, ROUTE_MAP, DefaultIcon } from "../../constants/productConstants";
import { showGenericError, findPlan, getEffectiveCancelsAt } from "../../helper";
import DeleteConfirmationChild from "../../Components/Generic/DeleteConfirmationWithChild";
import { UNSUBSCRIBE, REACTIVATE_SUBSCRIPTION } from "../../constants/OptionsConstants";

const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { setShowSalesModal } = useOutletContext();

  const {
    subscription,
    setSubscription,
    getPlatformSubscription,
    getPlatformProducts,
    getPlatformDashboardURL,
    numberOfConfirmedUsers,
    subscribeToPlan,
    addProduct,
    removeProduct,
  } = useLicensingContext();

  const { selectedProject } = useProjectContext();

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [apiProducts, setApiProducts] = useState([]);
  const [meetingsUsersCount, setMeetingsUsersCount] = useState(null);
  const [teamsUsersCount, setTeamsUsersCount] = useState(null);
  const [dynamicProducts, setDynamicProducts] = useState(null);
  const [activeModal, setActiveModal] = useState({
    isOpen: false,
    type: null, 
    slug: null, 
    isFull: false, 
  });

  const items = subscription?.items || [];

  const isFullyCanceled = useMemo(() => {
    if (subscription?.cancels_at != null && items.every((i) => i.cancels_at == null)) {
      return true;
    }
    return false;
  }, [subscription, items]);

  const isEnterprise = useMemo(
    () =>
      items.some(
        (item) =>
          findPlan(apiProducts, item.product_slug, item.plan_name)?.enterprise === true
      ),
    [items, apiProducts]
  );

  const benefitsMap = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      map[item.product_slug] =
        findPlan(apiProducts, item.product_slug, item.plan_name)
          ?.display_metadata?.benefits || [];
    });
    return map;
  }, [items, apiProducts]);

  const subscribedSlugs = useMemo(
    () => new Set(items.map((i) => i.product_slug)),
    [items]
  );

  const unsubscribedProducts = useMemo(
    () =>
      apiProducts.filter(
        (p) => p.active && !subscribedSlugs.has(p.slug)
      ),
    [apiProducts, subscribedSlugs]
  );

  const totalAmount = useMemo(() => {
    const sum = items.reduce((acc, item) => acc + (item.total_amount || 0), 0);
    const currency = subscription?.currency || "GBP";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(sum / 100);
  }, [items, subscription?.currency]);

  const displayProducts =
    dynamicProducts ||
    Object.entries(PRODUCTS)
      .map(([key, d]) => ({ key, ...d }));

  const fetchAll = () => {
    if (!selectedProject) return;
    setLoaded(false);
    setLoading(true);

    getPlatformSubscription(
      selectedProject.id,
      () => {
        numberOfConfirmedUsers(
          selectedProject.id,
          (resp) => setMeetingsUsersCount(resp.data.confirmed_users),
          () => {},
          "meetings"
        );
        numberOfConfirmedUsers(
          selectedProject.id,
          (resp) => setTeamsUsersCount(resp.data.confirmed_users),
          () => {},
          "emotii_for_teams"
        );
        getPlatformProducts(
          selectedProject.id,
          (response) => {
            const prods = response?.data?.products || [];
            setApiProducts(prods);

            const merged = prods
              .filter((p) => p.active)
              .map((p) => {
                const defaults = PRODUCTS[p.slug] || {};
                return {
                  key: p.slug,
                  title: defaults.title || p.name,
                  route: ROUTE_MAP[p.slug] || "/dashboard",
                  description: defaults.description || "",
                  icon: defaults.icon ? defaults.icon() : <DefaultIcon />,
                };
              });

            setDynamicProducts(merged.length > 0 ? merged : null);
            setLoading(false);
            setLoaded(true);
          },
          () => {
            setLoading(false);
            setLoaded(true);
          }
        );
      },
      () => {
        setSubscription(null);
        setLoading(false);
        setLoaded(true);
      }
    );
  };

  useEffect(() => {
    fetchAll();
  }, [selectedProject]);

  const handleRemoveProduct = (productSlug) => {
    if (!selectedProject) return;
    setLoading(true);
    removeProduct(
      selectedProject.id,
      productSlug,
      () => {
        setTimeout(() => {
          setLoading(false);
          fetchAll();
        }, 3000);
      },
      () => {
        setLoading(false);
        showGenericError();
      }
    );
  };

  const handleReactivateProduct = (productSlug) => {
    if (!selectedProject) return;

    const item = items.find((i) => i.product_slug === productSlug);
    const product = apiProducts.find((p) => p.slug === productSlug);
    const catalogPlan = (product?.product_plans || []).find(
      (pp) => pp.name === item?.plan_name && pp.interval === subscription.interval
    );
    const planId = catalogPlan?.id || item?.plan_id;

    if (!planId) {
      showGenericError();
      return;
    }

    const currency = subscription?.currency || "GBP";
    setLoading(true);

    addProduct(
      selectedProject.id,
      planId,
      currency,
      () => { setLoading(false); fetchAll(); },
      () => { setLoading(false); showGenericError(); }
    );
  };
  const handleReactivateSubscription = () => {
    if (!selectedProject || items.length === 0) return;
    setLoading(true);

    const currency = subscription?.currency || "GBP";

    const planIds = items
      .map((item) => findPlan(apiProducts, item.product_slug, item.plan_name)?.id || item?.plan_id)
      .filter(Boolean);

    if (planIds.length === 0) {
      setLoading(false);
      showGenericError();
      return;
    }

    const [firstPlanId, ...remainingPlanIds] = planIds;

    subscribeToPlan(
      selectedProject.id,
      firstPlanId,
      currency,
      (response) => {
        if (response.data.url) {
          if (remainingPlanIds.length > 0) {
            localStorage.setItem(
              "pendingReactivationPlans",
              JSON.stringify({
                projectId: selectedProject.id,
                planIds: remainingPlanIds,
                currency,
              })
            );
          }
          window.location = response.data.url;
        } else {
          const addRemainingProducts = (index) => {
            if (index >= remainingPlanIds.length) {
              setLoading(false);
              fetchAll();
              return;
            }
            addProduct(
              selectedProject.id,
              remainingPlanIds[index],
              currency,
              () => addRemainingProducts(index + 1),
              () => {
                setLoading(false);
                showGenericError();
              }
            );
          };
          addRemainingProducts(0);
        }
      },
      () => {
        setLoading(false);
        showGenericError();
      }
    );
  };

  const handleViewInvoices = () => {
    if (!selectedProject) return;
    setLoading(true);
    getPlatformDashboardURL(
      selectedProject.id,
      (response) => {
        if (response.data.url) window.location = response.data.url;
        setLoading(false);
      },
      () => {
        setLoading(false);
        showGenericError();
      }
    );
  };

  const pendingCancelCount = useMemo(
    () =>
      items.filter((item) => item.cancels_at != null || isFullyCanceled).length,
    [items, isFullyCanceled]
  );

  return (
    <LoaderComponent loading={loading}>
      <Container>
        <PageContent>
          {loaded && (
            <>
              {subscription ? (
                <>
                  <HeaderRow>
                    <PageHeader
                      title="Subscription Plans"
                      subtitle="Manage your plan and billing details here."
                    />
                    <HeaderActions>
                      {!isEnterprise && !isFullyCanceled && (
                        <EnterpriseButton onClick={() => setShowSalesModal(true)}>
                          <EnterpriseIcon>
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.49992 0.833008V3.33301M12.4999 0.833008V3.33301M7.49992 16.6663V19.1663M12.4999 16.6663V19.1663M16.6666 7.49967H19.1666M16.6666 11.6663H19.1666M0.833252 7.49967H3.33325M0.833252 11.6663H3.33325M4.99992 3.33301H14.9999C15.9204 3.33301 16.6666 4.0792 16.6666 4.99967V14.9997C16.6666 15.9201 15.9204 16.6663 14.9999 16.6663H4.99992C4.07944 16.6663 3.33325 15.9201 3.33325 14.9997V4.99967C3.33325 4.0792 4.07944 3.33301 4.99992 3.33301ZM7.49992 7.49967H12.4999V12.4997H7.49992V7.49967Z" stroke="#7F56D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </EnterpriseIcon>
                          Upgrade to Enterprise
                        </EnterpriseButton>
                      )}
                      {!(subscription?.has_manual_products && !subscription?.has_stripe_products) && (
                        <OutlineButton
                          text="Billing & Invoices"
                          onClick={handleViewInvoices}
                        />
                      )}
                    </HeaderActions>
                  </HeaderRow>

                  <SubscriptionSummaryBar
                    planName={[...new Set(items.map((item) => item.plan_name).filter(Boolean))].join(", ")}
                    itemCount={items.length}
                    interval={subscription?.interval}
                    totalAmount={totalAmount}
                    pendingCancelCount={pendingCancelCount}
                  />

                  <Section>
                    <SectionLabel>Active Plans</SectionLabel>
                    <CardsGrid count={items.length}>
                      {items.map((item) => {
                        const effectiveCancelsAt = getEffectiveCancelsAt(item, subscription);
                        const isItemPendingCancel = effectiveCancelsAt != null;

                        return (
                          <SubscriptionDetails
                            key={item.product_slug}
                            subscription={{
                              ...item,
                              amount_due: item.total_amount,
                              currency: subscription.currency,
                              interval: subscription.interval,
                              status: subscription.status,
                              next_payment_date: subscription.next_payment_date,
                              cancels_at: effectiveCancelsAt,
                            }}
                            benefits={benefitsMap[item.product_slug] || []}
                            onRemoveProduct={
                              !isItemPendingCancel
                                ? () => handleRemoveProduct(item.product_slug)
                                : undefined
                            }
                            onReactivate={
                              isItemPendingCancel
                                ? isFullyCanceled
                                  ? handleReactivateSubscription
                                  : () => handleReactivateProduct(item.product_slug)
                                : undefined
                            }
                            confirmedUsers={
                              item.product_slug === "meetings" ? meetingsUsersCount :
                              item.product_slug === "emotii_for_teams" ? teamsUsersCount :
                              null
                            }
                          />
                        );
                      })}
                    </CardsGrid>
                  </Section>

                  {unsubscribedProducts.length > 0 && (
                    <Section>
                      <SectionLabel>Available Products</SectionLabel>
                      <CardsGrid count={unsubscribedProducts.length}>
                        {unsubscribedProducts.map((p) => (
                          <NoProductSubscriptionCard
                            key={p.slug}
                            slug={p.slug}
                            label={p.name}
                            variant="subscription"
                          />
                        ))}
                      </CardsGrid>
                    </Section>
                  )}
                </>
              ) : (
                <ProductHubSection>
                  <HubIntro>
                    <HubTitle>Subscription Plans</HubTitle>
                    <HubSubtitle>
                      Choose a product below to explore available plans and pricing.
                    </HubSubtitle>
                  </HubIntro>

                  <ProductCardsContainer>
                    {displayProducts.map((p, i) => (
                      <SubscriptionProductCard
                        key={`${p.key}-${i}`}
                        icon={p.icon}
                        title={p.title}
                        description={p.description}
                        onClick={() => navigate(p.route)}
                      />
                    ))}
                  </ProductCardsContainer>
                </ProductHubSection>
              )}
            </>
          )}
        </PageContent>
      </Container>
    </LoaderComponent>
  );
};

export default SubscriptionsPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  padding: 32px 24px;
  height: 100%;
  flex: 1;
  background: #F9FAFB;

  @media (max-width: 768px) {
    padding: 20px 15px;
    gap: 14px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 4px;
`;

const EnterpriseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f4ebff;
  color: #7f56d9;
  border: 1px solid #d6bbfb;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.12s ease;
  white-space: nowrap;

  &:hover {
    background: #ede0ff;
    transform: translateY(-1px);
  }
`;

const EnterpriseIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

const CardsGrid = styled.div`
  display: grid;
  gap: 12px;
  width: 100%;

  grid-template-columns: ${(p) => {
    if (p.count === 1) return "minmax(300px, 520px)";
    if (p.count === 2) return "repeat(2, minmax(280px, 520px))";
    return "repeat(3, minmax(260px, 500px))";
  }};

  @media (max-width: 1280px) {
    grid-template-columns: ${(p) =>
      p.count === 1
        ? "minmax(300px, 600px)"
        : "repeat(2, minmax(280px, 600px))"};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductHubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  width: 100%;
  flex: 1;
  justify-content: center;
  padding: 0 0 60px 0;
`;

const HubIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const HubTitle = styled.h2`
  color: #181D27;
  font-family: "Lato", sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;
  margin: 0;
`;

const HubSubtitle = styled.p`
  color: #535862;
  font-family: "Lato", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
  max-width: 480px;
`;

const ProductCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 1200px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.h3`
  color: #101828;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
`;